import { useIntersectionObserver } from '@vueuse/core';
import { Ref, computed, watch } from 'vue';
import { useState } from 'vuex-composition-helpers';
import { nameRoutesStore } from '@ve/components/routes/common/stores/RoutesStore';
import { type IRoutesStoreState } from '@ve/components/routes/common/stores/types/IRoutesStoreState';
import useLeadBannerTracking from './useLeadBannerTracking';
import type { ContextInput } from './useLeadBannerTracking';
import {
    LEAD_BANNER_PLACEMENT,
    LEAD_BANNER_TRIGGER_REASON,
} from './analytics/types';

interface UseTourLeadBannerTrackingOptions {
    formRef: Ref<{ $el?: Element } | null>;
    isSubmitted: Ref<boolean>;
}

interface UseTourLeadBannerTrackingReturn {
    onFormStart: () => void;
    onSheetOpen: () => void;
}

const TOUR_CONTEXT: ContextInput = {
    contextKey: 'tour_page',
    placement: LEAD_BANNER_PLACEMENT.TOUR_PAGE,
    position: null,
    triggerReason: LEAD_BANNER_TRIGGER_REASON.DEFAULT,
};

export default function useTourLeadBannerTracking(
    options: UseTourLeadBannerTrackingOptions,
): UseTourLeadBannerTrackingReturn {
    const tracking = useLeadBannerTracking();

    const { actualizeState } = useState<IRoutesStoreState>(nameRoutesStore, ['actualizeState']);

    // Импрешн отправляем по факту попадания DOM-узла баннера в viewport,
    // а не по готовности данных — иначе событие срабатывает, пока пользователь
    // ещё в верху страницы и баннера не видит.
    const targetEl = computed(
        () => (options.formRef.value?.$el ?? undefined) as HTMLElement | undefined,
    );
    useIntersectionObserver(targetEl, ([entry]) => {
        if (entry?.isIntersecting) {
            tracking.onImpression(TOUR_CONTEXT);
        }
    });

    watch(options.isSubmitted, (next, prev) => {
        if (next && !prev) {
            tracking.onFormSubmit(TOUR_CONTEXT);
        }
    });

    watch(actualizeState, (next) => {
        if (next === 'inProgress') {
            tracking.resetContexts();
        }
    });

    return {
        onFormStart: () => tracking.onFormStart(TOUR_CONTEXT),
        onSheetOpen: () => tracking.onSheetOpen(TOUR_CONTEXT),
    };
}
